import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React (Leaflet bundler issue)
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

interface GPSPoint {
  latitude: number;
  longitude: number;
  timestamp: Date | number;
  altitude?: number;
  distanceFromStart?: number;
}

interface SplitMarker {
  km: number;
  time?: number;
  pace?: number;
}

interface RouteMapProps {
  gpsPoints: GPSPoint[];
  splits?: SplitMarker[];
  height?: string;
  showElevation?: boolean;
  className?: string;
}

/**
 * Format seconds as M:SS pace string
 */
const formatPace = (paceSeconds: number): string => {
  const minutes = Math.floor(paceSeconds / 60);
  const seconds = Math.floor(paceSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}/km`;
};

/**
 * Haversine distance between two lat/lng points (meters)
 */
const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/**
 * Find the GPS point index closest to a target distance (meters).
 * Uses pre-computed distanceFromStart if available, otherwise Haversine.
 */
const findPointAtDistance = (points: GPSPoint[], targetMeters: number): number => {
  if (!points || points.length === 0) return 0;
  if (points[0]?.distanceFromStart !== undefined) {
    // Use pre-computed distances
    let closest = 0;
    let closestDiff = Math.abs((points[0].distanceFromStart ?? 0) - targetMeters);
    for (let i = 1; i < points.length; i++) {
      const diff = Math.abs((points[i].distanceFromStart ?? 0) - targetMeters);
      if (diff < closestDiff) {
        closestDiff = diff;
        closest = i;
      }
      // Early exit — distances are monotonically increasing
      if ((points[i].distanceFromStart ?? 0) > targetMeters) break;
    }
    return closest;
  }

  // Fallback: cumulative Haversine
  let cumulative = 0;
  for (let i = 1; i < points.length; i++) {
    cumulative += haversineDistance(
      points[i - 1].latitude, points[i - 1].longitude,
      points[i].latitude, points[i].longitude
    );
    if (cumulative >= targetMeters) return i;
  }
  return points.length - 1;
};

export const RouteMap: React.FC<RouteMapProps> = ({
  gpsPoints,
  splits,
  height = '400px',
  showElevation = false,
  className = ''
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);

  // Defined before useEffect so it's available in the effect closure without stale-ref risk
  const formatTimestamp = (timestamp: Date | number): string => {
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  useEffect(() => {
    if (!mapContainerRef.current || !gpsPoints || gpsPoints.length === 0) {
      return;
    }

    // Initialize map
    if (!mapRef.current) {
      mapRef.current = L.map(mapContainerRef.current, {
        scrollWheelZoom: false,
        dragging: true,
        zoomControl: true,
      });

      // Add OpenStreetMap tiles (free)
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      }).addTo(mapRef.current);
    }

    const map = mapRef.current;

    // Clear existing layers
    map.eachLayer((layer) => {
      if (layer instanceof L.Polyline || layer instanceof L.Marker) {
        map.removeLayer(layer);
      }
    });

    // Defensive downsample: cap at 1500 points to keep Leaflet responsive
    // (primary downsampling should happen in the parent component)
    let displayPoints = gpsPoints;
    if (gpsPoints.length > 1500) {
      const step = Math.ceil(gpsPoints.length / 1500);
      displayPoints = gpsPoints.filter((_, i) => i % step === 0);
      // Always include last point for accurate finish marker
      if (displayPoints[displayPoints.length - 1] !== gpsPoints[gpsPoints.length - 1]) {
        displayPoints.push(gpsPoints[gpsPoints.length - 1]);
      }
    }

    // Convert GPS points to Leaflet format [lat, lng]
    const route = displayPoints.map(point => [point.latitude, point.longitude] as [number, number]);
    
    // Calculate bounds and fit map
    const bounds = L.latLngBounds(route);
    map.fitBounds(bounds, { padding: [30, 30] });

    // Draw route polyline (Coach Cedar green)
    const polyline = L.polyline(route, {
      color: '#1F6C4F',
      weight: 4,
      opacity: 0.8,
      lineJoin: 'round'
    }).addTo(map);

    // Add start marker (green)
    const startPoint = route[0];
    const startIcon = L.divIcon({
      className: 'custom-marker',
      html: `<div style="background-color: #16a34a; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
      iconSize: [24, 24],
      iconAnchor: [12, 12]
    });
    L.marker(startPoint, { icon: startIcon })
      .bindPopup(`<strong>Start</strong><br/>${formatTimestamp(displayPoints[0].timestamp)}`)
      .addTo(map);

    // Add finish marker (red)
    const endPoint = route[route.length - 1];
    const endIcon = L.divIcon({
      className: 'custom-marker',
      html: `<div style="background-color: #dc2626; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
      iconSize: [24, 24],
      iconAnchor: [12, 12]
    });
    L.marker(endPoint, { icon: endIcon })
      .bindPopup(`<strong>Finish</strong><br/>${formatTimestamp(displayPoints[displayPoints.length - 1].timestamp)}`)
      .addTo(map);

    // Add split/km markers
    if (splits && splits.length > 0) {
      splits.forEach((split) => {
        const targetMeters = split.km * 1000;
        const rawIdx = findPointAtDistance(displayPoints, targetMeters);
        // Clamp to valid range — guards against any edge case in distance lookup
        const pointIdx = Math.min(Math.max(rawIdx, 0), displayPoints.length - 1);
        const point = displayPoints[pointIdx];
        if (!point) return;

        const splitIcon = L.divIcon({
          className: 'split-marker',
          html: `<div style="
            background-color: #2563EB;
            color: white;
            width: 22px;
            height: 22px;
            border-radius: 50%;
            border: 2px solid white;
            box-shadow: 0 1px 3px rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 11px;
            font-weight: 700;
            line-height: 1;
          ">${split.km}</div>`,
          iconSize: [22, 22],
          iconAnchor: [11, 11]
        });

        const paceStr = split.pace ? formatPace(split.pace) : '';
        const popupContent = `<strong>Split ${split.km} km</strong>${paceStr ? `<br/>Pace: ${paceStr}` : ''}`;

        L.marker([point.latitude, point.longitude], { icon: splitIcon })
          .bindPopup(popupContent)
          .addTo(map);
      });
    }

    // Invalidate size to fix rendering issues — store timer so cleanup can cancel it
    const invalidateTimer = setTimeout(() => {
      if (mapRef.current) map.invalidateSize();
    }, 100);

    // Cleanup on unmount — cancel any pending timer before destroying the map
    return () => {
      clearTimeout(invalidateTimer);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [gpsPoints, splits, height]);

  if (!gpsPoints || gpsPoints.length === 0) {
    return (
      <div 
        className={`bg-gray-50 rounded-lg flex items-center justify-center ${className}`} 
        style={{ height }}
      >
        <div className="text-center p-6">
          <svg 
            className="w-12 h-12 mx-auto mb-3 text-gray-400" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" 
            />
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" 
            />
          </svg>
          <p className="text-sm text-gray-600 mb-1">No GPS Data</p>
          <p className="text-xs text-gray-500">
            This activity was completed without GPS tracking
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={mapContainerRef} 
      className={`rounded-lg border border-gray-200 ${className}`}
      style={{ height, width: '100%' }}
    />
  );
};

export default RouteMap;

